/**
 * Heap URL query-string scrub transformer (privacy hardening).
 *
 * WHY THIS EXISTS
 * ---------------
 * Heap ALWAYS captures the initial page-load (and session-expiration) pageview
 * with the resolved `window.location`, even with `disablePageviewAutocapture:
 * true` — per Heap's docs that option only suppresses SPA `pushState` /
 * `popstate` / `hashchange` pageviews; "pageviews occurring on page load and
 * session expiration will continue to be captured automatically." Those
 * autocaptured pageview / session messages (the standalone "View <path>" events
 * in Heap) carry the URL's `query` and `hash`, which can hold PII or secrets —
 * notably a magic-link `?token=...`, a partner link `?t=...` (a long-lived
 * credential) or an `?email=...` on a landing URL.
 *
 * WHAT WE KEEP vs SCRUB
 * ---------------------
 *   - PATH is KEPT verbatim (e.g. `/properties/<uuid>`). Our paths only ever
 *     contain opaque ENTITY ids (property / offer / buybox), never a person's
 *     address, name, or email — those are never in the URL. Keeping the resolved
 *     path is intentional: it lets Heap measure per-entity engagement (e.g.
 *     visits per property). (This is a deliberate relaxation of "template-only"
 *     pageviews; flip `scrubHeapMessage` to also redact path segments if that
 *     posture ever needs to change.)
 *   - QUERY is filtered to a drop-unless-listed allow-list of marketing /
 *     attribution params (utm_*, gclid, ...). Everything else — `email`,
 *     `token`, `t`, ... — is dropped.
 *   - HASH is blanked (no analytics value; can carry OAuth-implicit tokens).
 *
 * WHERE IT RUNS — pipelines matter
 * --------------------------------
 * Registered from the loader on BOTH Heap pipelines:
 *   - 'metadata' — processes the autocaptured pageview / session / user messages
 *     (the "View <path>" events and `initial_pageview_info` / `pageview_info`).
 *     THIS is the pipeline the initial-pageview URL rides on. Registering the
 *     scrub ONLY on 'general' (the default) was why the initial "View" pageview
 *     leaked the raw query — 'general' never sees those metadata messages.
 *   - 'general' — processes our custom track events, which also carry a
 *     `pageview_info.url` snapshot.
 * The same recursive scrub handles both message shapes (it walks to any nested
 * `{domain,path,query,hash}` url object, e.g. `data.initial_pageview_info.url`).
 *
 * The transformer is a pure function over the (already-cloned by Heap) message;
 * it mutates in place and returns the same reference, and never throws.
 */

/** The base registry name we register the scrub transformer under (one per pipeline). */
export const URL_SCRUB_TRANSFORMER_NAME = 'orchard-url-scrub'

/**
 * Pipelines the scrub must run on. 'metadata' is REQUIRED (autocaptured pageview/
 * session messages — incl. the initial pageview — flow there); 'general' covers
 * the url snapshot riding on our custom events.
 */
export const URL_SCRUB_PIPELINES = ['metadata', 'general'] as const

/**
 * Query params we KEEP (drop-unless-listed). Known marketing / attribution
 * params only — opaque, non-PII campaign/click ids. Matched case-insensitively.
 * Deliberately EXCLUDES ambiguous identity-ish names (`email`, `id`, `cid`,
 * `sid`, `uid`) and link credentials (`token` for magic links, `t` for partner
 * links) — those are the PII and secret vectors this scrub exists to stop.
 */
export const ALLOWED_QUERY_PARAMS: ReadonlySet<string> = new Set([
  // UTM (standard + GA4 extended)
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'utm_id', 'utm_source_platform', 'utm_creative_format', 'utm_marketing_tactic',
  // Google Ads / DoubleClick
  'gclid', 'gclsrc', 'gbraid', 'wbraid', 'dclid', 'gad_source',
  // Microsoft / Bing
  'msclkid',
  // Meta (Facebook / Instagram)
  'fbclid', 'igshid',
  // TikTok
  'ttclid',
  // Twitter / X
  'twclid',
  // LinkedIn
  'li_fat_id',
  // Pinterest
  'epik',
  // Reddit
  'rdt_cid',
  // Snapchat
  'sccid',
  // Yandex
  'yclid',
  // Mailchimp
  'mc_cid', 'mc_eid',
  // HubSpot
  '_hsenc', '_hsmi', 'hsa_acc', 'hsa_cam', 'hsa_grp', 'hsa_ad', 'hsa_src',
  'hsa_tgt', 'hsa_kw', 'hsa_mt', 'hsa_net', 'hsa_ver',
  // Klaviyo
  '_kx',
  // Marketo
  'mkt_tok',
  // Affiliate networks
  'irclickid', 'cjevent',
  // Generic marketing referral
  'ref', 'referrer', 'source', 'campaign', 'medium', 'affiliate', 'aff',
  'partner', 'promo', 'promo_code',
])

/**
 * Filter a query string to the allow-listed tracking params. Input may include
 * the leading `?`. Returns `?kept=…` (allowed params, original order/case
 * preserved) or `''` when nothing survives. Non-strings return `''`.
 */
export function filterQueryString(query: unknown): string {
  if (typeof query !== 'string' || query === '') return ''
  const raw = query.charAt(0) === '?' ? query.slice(1) : query
  if (raw === '') return ''
  try {
    const kept = new URLSearchParams()
    for (const [key, value] of new URLSearchParams(raw)) {
      if (ALLOWED_QUERY_PARAMS.has(key.toLowerCase())) kept.append(key, value)
    }
    const out = kept.toString()
    return out ? `?${out}` : ''
  } catch {
    // Malformed query → safest is to drop it entirely.
    return ''
  }
}

/** Is this a Heap "url" object (`{domain, path, query, hash}`) vs. an arbitrary bag? */
function isUrlObject(node: Record<string, unknown>): boolean {
  return ('domain' in node && 'path' in node) || 'query' in node || 'hash' in node
}

/**
 * Scrub a scalar full-URL string (`previous_page` / `referrer`): keep scheme +
 * host + path, filter the query to the allow-list, and drop the hash. Returns
 * non-strings unchanged.
 */
export function scrubUrlString(value: unknown): unknown {
  if (typeof value !== 'string' || value === '') return value
  const noHash = value.split('#')[0]
  const qIdx = noHash.indexOf('?')
  const base = qIdx >= 0 ? noHash.slice(0, qIdx) : noHash
  const query = qIdx >= 0 ? noHash.slice(qIdx) : ''
  return `${base}${filterQueryString(query)}`
}

/**
 * Recursively scrub a Heap event message in place: on every url object, filter
 * `query` to the allow-list and blank `hash` (PATH is intentionally kept); and
 * filter query / drop hash on scalar `previous_page` / `referrer` URL fields.
 * Returns the same message reference (mutated). Safe on any input; never throws.
 */
export function scrubHeapMessage(message: unknown): unknown {
  const seen = new Set<unknown>()
  const walk = (node: unknown, depth: number): void => {
    if (!node || typeof node !== 'object' || depth > 10 || seen.has(node)) return
    seen.add(node)
    const obj = node as Record<string, unknown>

    if (isUrlObject(obj)) {
      if ('query' in obj) obj.query = filterQueryString(obj.query)
      if ('hash' in obj) obj.hash = ''
      // PATH is kept verbatim by design (opaque entity ids, not personal PII).
    }

    for (const key of Object.keys(obj)) {
      const value = obj[key]
      if ((key === 'previous_page' || key === 'referrer') && typeof value === 'string') {
        obj[key] = scrubUrlString(value)
      } else if (value && typeof value === 'object') {
        walk(value, depth + 1)
      }
    }
  }
  try {
    walk(message, 0)
  } catch {
    // Analytics must never break a user session over a scrub error; return as-is.
  }
  return message
}
